import React, { useCallback, useEffect, useMemo, useState } from 'react';

import {
    Box,
    FormControl,
    InputLabel,
    LinearProgress,
    MenuItem,
    Paper,
    Select,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow
} from '@mui/material';
import CancelPresentationRoundedIcon from '@mui/icons-material/CancelPresentationRounded';
import CheckCircleRoundedIcon from '@mui/icons-material/CheckCircleRounded';
import ElectricBoltRoundedIcon from '@mui/icons-material/ElectricBoltRounded';
import GroupsRoundedIcon from '@mui/icons-material/GroupsRounded';
import PendingActionsRoundedIcon from '@mui/icons-material/PendingActionsRounded';
import PieChartRoundedIcon from '@mui/icons-material/PieChartRounded';
import StackedBarChartRoundedIcon from '@mui/icons-material/StackedBarChartRounded';
import { useSearchParams } from 'react-router-dom';

import BiMetric from '../../components/biMetric';
import MonthRangePicker from '../../components/monthRangePicker';
import PageHeader from '../../components/pageHeader';
import {
    formatBackendDatePtBr,
    formatMonthParam,
    getCurrentMonthStart,
    parseMonthParamOrCurrent
} from '../../utils/dateUtils';
import {
    formatInteger,
    formatPercentRatio
} from '../../utils/numberUtils';
import {
    allDistributorsOption,
    buildDistributorOptions,
    buildDistributorBreakdown,
    buildOpenUnitRows,
    buildTrocasComposition,
    buildTrocasSummary,
    fetchTrocasEmAberto,
    fetchTrocasMes,
    filterTrocasByDistributor
} from './trocasTitularidadeData';
import '../css/biAluguelAccess.css';

const minMonth = new Date(2025, 0, 1);

const TrocasCompositionChart = ({ items, total }) => {
    let offset = 0;

    return (
        <div className='bi-ge-trocas-composition'>
            <div className='bi-ge-trocas-pie-wrap' aria-label='Composição das trocas no período'>
                <svg className='bi-ge-trocas-pie' viewBox='0 0 120 120' role='img'>
                    <circle className='bi-ge-trocas-pie-track' cx='60' cy='60' r='42' pathLength='100' />
                    {items.map((item) => {
                        const share = total ? (item.value / total) * 100 : 0;
                        const slice = (
                            <circle
                                key={item.key}
                                className='bi-ge-trocas-pie-slice'
                                cx='60'
                                cy='60'
                                r='42'
                                pathLength='100'
                                style={{
                                    '--slice-color': item.color,
                                    '--slice-size': share,
                                    '--slice-offset': offset
                                }}
                            />
                        );
                        offset += share;
                        return slice;
                    })}
                </svg>
                <div className='bi-ge-trocas-pie-center'>
                    <span>Entrantes</span>
                    <strong>{formatInteger(total)}</strong>
                </div>
            </div>

            <div className='bi-ge-trocas-composition-legend'>
                {items.map((item) => (
                    <div key={item.key} className='bi-ge-trocas-composition-item'>
                        <span style={{ '--item-color': item.color }} />
                        <p>{item.label}</p>
                        <strong>{formatInteger(item.value)}</strong>
                        <small>{formatPercentRatio(total ? item.value / total : 0)}</small>
                    </div>
                ))}
            </div>
        </div>
    );
};

const DistributorBreakdown = ({ distributors, maxEntrantes }) => (
    <div className='bi-ge-trocas-breakdown'>
        {distributors.map((distributor) => (
            <div key={distributor.distribuidora} className='bi-ge-trocas-breakdown-row'>
                <div className='bi-ge-trocas-breakdown-heading'>
                    <span>{distributor.distribuidora}</span>
                    <strong>{formatInteger(distributor.entrantes)}</strong>
                </div>
                <div className='bi-ge-trocas-breakdown-track'>
                    <span
                        className='bi-ge-trocas-breakdown-bar is-completed'
                        style={{ '--bar-size': `${maxEntrantes ? (distributor.trocas_efetuadas / maxEntrantes) * 100 : 0}%` }}
                    />
                    <span
                        className='bi-ge-trocas-breakdown-bar is-terminated'
                        style={{ '--bar-size': `${maxEntrantes ? (distributor.distratados / maxEntrantes) * 100 : 0}%` }}
                    />
                </div>
                <div className='bi-ge-trocas-breakdown-meta'>
                    <span>{formatInteger(distributor.trocas_efetuadas)} trocas</span>
                    <span>{formatInteger(distributor.distratados)} distratos</span>
                </div>
            </div>
        ))}
    </div>
);

const OpenUnitsTable = ({ rows, isLoading }) => (
    <TableContainer className='bi-ge-trocas-open-table-shell'>
        <Table size='small' stickyHeader>
            <TableHead>
                <TableRow>
                    <TableCell>Unidade</TableCell>
                    <TableCell>Conta contrato</TableCell>
                    <TableCell>Cliente</TableCell>
                    <TableCell>Distribuidora</TableCell>
                    <TableCell>Assinatura</TableCell>
                    <TableCell align='right'>Dias em aberto</TableCell>
                </TableRow>
            </TableHead>
            <TableBody>
                {rows.map((row) => (
                    <TableRow key={row.unidade} hover>
                        <TableCell>{row.unidade}</TableCell>
                        <TableCell>{row.cc}</TableCell>
                        <TableCell>{row.cliente}</TableCell>
                        <TableCell>{row.distribuidora}</TableCell>
                        <TableCell>{formatBackendDatePtBr(row.data_assinatura)}</TableCell>
                        <TableCell align='right'>{row.diasEmAberto === null ? '' : formatInteger(row.diasEmAberto)}</TableCell>
                    </TableRow>
                ))}
                {!rows.length && !isLoading && (
                    <TableRow>
                        <TableCell colSpan={6} align='center'>
                            Nenhuma unidade em aberto para os filtros selecionados.
                        </TableCell>
                    </TableRow>
                )}
            </TableBody>
        </Table>
    </TableContainer>
);

const TrocasTitularidadeBIGE = () => {
    const [searchParams] = useSearchParams();
    const [dataInicio, setDataInicio] = useState(() => parseMonthParamOrCurrent(searchParams.get('data_inicio')));
    const [dataFim, setDataFim] = useState(() => parseMonthParamOrCurrent(searchParams.get('data_fim')));
    const [selectedDistributor, setSelectedDistributor] = useState(allDistributorsOption);
    const [trocas, setTrocas] = useState([]);
    const [unidadesEmAberto, setUnidadesEmAberto] = useState([]);
    const [isLoading, setIsLoading] = useState(false);

    const loadTrocas = useCallback(async (startDate, endDate) => {
        setIsLoading(true);
        const period = {
            dataInicio: formatMonthParam(startDate),
            dataFim: formatMonthParam(endDate)
        };
        const [monthTrocas, openUnits] = await Promise.all([
            fetchTrocasMes(period),
            fetchTrocasEmAberto(period)
        ]);
        setTrocas(monthTrocas);
        setUnidadesEmAberto(openUnits);
        setIsLoading(false);
    }, []);

    useEffect(() => {
        loadTrocas(dataInicio, dataFim);
    }, [dataFim, dataInicio, loadTrocas]);

    const filteredTrocas = useMemo(
        () => filterTrocasByDistributor(trocas, selectedDistributor),
        [selectedDistributor, trocas]
    );
    const summary = useMemo(() => buildTrocasSummary(filteredTrocas), [filteredTrocas]);
    const distributorOptions = useMemo(() => buildDistributorOptions(trocas), [trocas]);
    const composition = useMemo(() => buildTrocasComposition(summary), [summary]);
    const distributorBreakdown = useMemo(() => buildDistributorBreakdown(filteredTrocas), [filteredTrocas]);
    const maxEntrantes = useMemo(
        () => Math.max(...distributorBreakdown.map((distributor) => distributor.entrantes), 0),
        [distributorBreakdown]
    );

    const openUnitRows = useMemo(
        () => buildOpenUnitRows(filterTrocasByDistributor(unidadesEmAberto, selectedDistributor)),
        [selectedDistributor, unidadesEmAberto]
    );

    const handleDateRangeApply = (startDate, endDate) => {
        setDataInicio(startDate);
        setDataFim(endDate);
        setSelectedDistributor(allDistributorsOption);
    };

    return (
        <Box className='mainBox bi-aluguel-access-page bi-ge-indications-page bi-ge-distratos-page bi-ge-trocas-page'>
            <PageHeader>BI Aluguel - Trocas de Titularidade</PageHeader>

            <section className='bi-aluguel-access-shell bi-ge-indications-shell'>
                {isLoading && <LinearProgress className='bi-aluguel-access-loading' />}

                <div className='bi-ge-distratos-filter-row bi-ge-trocas-filter-row'>
                    <div className='bi-aluguel-access-filters bi-ge-indications-filters bi-ge-distratos-period-filter'>
                        <MonthRangePicker
                            startDate={dataInicio}
                            endDate={dataFim}
                            minDate={minMonth}
                            maxDate={getCurrentMonthStart()}
                            disabled={isLoading}
                            onApply={handleDateRangeApply}
                        />
                    </div>

                    <FormControl className='bi-ge-distratos-reason-filter bi-ge-trocas-distributor-filter'>
                        <InputLabel id='bi-ge-trocas-distributor-label'>Distribuidora</InputLabel>
                        <Select
                            labelId='bi-ge-trocas-distributor-label'
                            label='Distribuidora'
                            value={selectedDistributor}
                            disabled={isLoading}
                            onChange={(event) => setSelectedDistributor(event.target.value)}
                        >
                            {distributorOptions.map((option) => (
                                <MenuItem key={option.value} value={option.value}>
                                    {option.label}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                </div>

                <div className='bi-aluguel-access-metrics bi-ge-indications-metrics bi-ge-trocas-metrics'>
                    <BiMetric
                        icon={GroupsRoundedIcon}
                        label='Unidades entrantes'
                        value={formatInteger(summary.totalUnits)}
                        subtitle='Registros no período'
                        tone='green'
                    />
                    <BiMetric
                        icon={CheckCircleRoundedIcon}
                        label='Trocas efetivadas'
                        value={formatInteger(summary.completed)}
                        subtitle={`${formatPercentRatio(summary.completionRate)} de conclusão`}
                        tone='blue'
                    />
                    <BiMetric
                        icon={CancelPresentationRoundedIcon}
                        label='Unidades distratadas'
                        value={formatInteger(summary.terminated)}
                        subtitle='Marcadas como distrato'
                    />
                    <BiMetric
                        icon={ElectricBoltRoundedIcon}
                        label='KPI'
                        value={formatPercentRatio(summary.kpi)}
                        subtitle='Entrantes sem pendência / entrantes'
                        tone='navy'
                    />
                </div>

                <div className='bi-ge-trocas-visual-grid'>
                    <Paper className='bi-aluguel-access-panel bi-ge-trocas-chart-panel'>
                        <div className='bi-aluguel-access-panel-heading'>
                            <div>
                                <p>Composição</p>
                                <h2>Entrantes no período</h2>
                            </div>
                            <PieChartRoundedIcon />
                        </div>

                        <TrocasCompositionChart items={composition} total={summary.totalUnits} />
                    </Paper>

                    <Paper className='bi-aluguel-access-panel bi-ge-trocas-chart-panel'>
                        <div className='bi-aluguel-access-panel-heading'>
                            <div>
                                <p>Distribuidoras</p>
                                <h2>Volume agregado</h2>
                            </div>
                            <StackedBarChartRoundedIcon />
                        </div>

                        <DistributorBreakdown
                            distributors={distributorBreakdown}
                            maxEntrantes={maxEntrantes}
                        />
                    </Paper>
                </div>

                <Paper className='bi-aluguel-access-panel bi-ge-trocas-open-panel'>
                    <div className='bi-aluguel-access-panel-heading'>
                        <div>
                            <p>Em aberto</p>
                            <h2>{formatInteger(openUnitRows.length)} unidades sem troca nem distrato</h2>
                        </div>
                        <PendingActionsRoundedIcon />
                    </div>

                    <OpenUnitsTable rows={openUnitRows} isLoading={isLoading} />
                </Paper>
            </section>
        </Box>
    );
};

export default TrocasTitularidadeBIGE;
